import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type IGroupOrLabel, type ILogbook } from "src/interfaces";
import { deepClone } from "src/utils";
import { v4 as uuid4 } from "uuid";

import { getSelected, setSelected } from "../utils/groups";

interface OrganizeSlice {
  logbooks: ILogbook[];
  groups: IGroupOrLabel[];
  labels: IGroupOrLabel[];
  selectedGroup: IGroupOrLabel | null;
  selectedLabel: IGroupOrLabel | null;
}

const initialState: OrganizeSlice = {
  logbooks: [],
  groups: [],
  labels: [],
  selectedLabel: null,
  selectedGroup: null,
};

export const organizeSlice = createSlice({
  name: "organize",
  initialState,
  reducers: {
    setInitialData: (
      state,
      action: PayloadAction<{
        logbooks: ILogbook[];
        groups?: IGroupOrLabel[];
        labels?: IGroupOrLabel[];
      }>,
    ) => {
      const { groups, logbooks, labels } = action.payload;
      state.selectedGroup = null;
      state.selectedLabel = null;
      const parseLogbooks = (dataArray: IGroupOrLabel[]) => {
        let usedLogbooks: string[] = [];
        dataArray.forEach(el => {
          usedLogbooks = [
            ...usedLogbooks,
            ...el.logbooks.map(log => log?.lbid ?? { lbid: null }),
          ];
        });

        const logs: any[] = [];
        logbooks.forEach(log => {
          if (log?.lbid && !usedLogbooks.includes(log?.lbid)) {
            logs.push(log);
          }
        });
        state.logbooks = logs;
      };

      if (labels) {
        state.labels = labels;
        parseLogbooks(labels);
      }
      if (groups) {
        state.groups = groups;
        parseLogbooks(groups);
      }
    },
    setSelectedGroup: (state, action: PayloadAction<IGroupOrLabel | null>) => {
      state.selectedGroup = action.payload;
    },
    setSelectedLabel: (state, action: PayloadAction<IGroupOrLabel | null>) => {
      state.selectedLabel = action.payload;
    },
    setValue: (
      state,
      action: PayloadAction<{
        groupId: string;
        key: "group" | "color";
        value: string;
        keyForData: "groups" | "labels";
      }>,
    ) => {
      const { groupId, key, value, keyForData } = action.payload;
      const data = state[keyForData];
      const groupIndex = data.findIndex(g => g.groupId === groupId);
      if (groupIndex !== -1) {
        data[groupIndex][key] = value;
        data[groupIndex].changed = true;
        state[keyForData] = data;
      }
    },
    addElement: (
      state,
      action: PayloadAction<{
        keyForData: "groups" | "labels";
        defaultName: string;
      }>,
    ) => {
      const { keyForData, defaultName } = action.payload;
      const data: IGroupOrLabel = {
        groupId: uuid4(),
        group: defaultName,
        color: null,
        logbooks: [],
        changed: true,
        active: 1,
      };
      state[keyForData] = [data, ...state[keyForData]];
      setSelected(state, keyForData, data);
    },
    removeElement: (
      state,
      action: PayloadAction<{ id: string; keyForData: "groups" | "labels" }>,
    ) => {
      const { keyForData, id } = action.payload;
      const groupIndex = state[keyForData].findIndex(g => g.groupId === id);

      if (keyForData === "labels") {
        state[keyForData][groupIndex] = state[keyForData][groupIndex] = {
          ...state[keyForData][groupIndex],
          active: 0,
          changed: true,
        };
      } else {
        state[keyForData].splice(groupIndex, 1);
      }

      const selected = getSelected(state, keyForData);
      if (selected?.groupId === id) {
        setSelected(state, keyForData, null);
      }
    },
    moveLogbook: (
      state,
      action: PayloadAction<{
        logbook: ILogbook;
        keyForData: "labels" | "groups";
      }>,
    ) => {
      if (!action.payload?.logbook.lbid) return;
      const { logbook, keyForData } = action.payload;
      const data = deepClone(state[keyForData]);
      const selected = getSelected(state, keyForData);

      const groupIndex = data.findIndex(
        (g: IGroupOrLabel) => g.groupId === selected?.groupId,
      );

      if (groupIndex !== -1 && selected) {
        if (
          selected.logbooks.map(l => l?.lbid ?? null).includes(logbook.lbid)
        ) {
          setSelected(state, keyForData, {
            ...selected,
            logbooks: selected.logbooks.filter(l => l.lbid !== logbook.lbid),
          });
          data[groupIndex].logbooks = selected.logbooks.filter(
            l => l.lbid !== logbook.lbid,
          );
          data[groupIndex].changed = true;
          state[keyForData] = data;
          state.logbooks = [...state.logbooks, logbook];
        } else {
          selected.logbooks.push(logbook);
          data[groupIndex].logbooks.push(logbook);
          data[groupIndex].changed = true;

          setSelected(state, keyForData, selected);
          state[keyForData] = data;
          state.logbooks = state.logbooks.filter(
            log => log.lbid !== logbook.lbid,
          );
        }
      }
    },
  },
});

export const {
  setInitialData,
  setSelectedGroup,
  setValue,
  addElement,
  removeElement,
  moveLogbook,
} = organizeSlice.actions;

export default organizeSlice.reducer;
